import type { ErrorEvent } from "@sentry/nextjs";
import * as Sentry from "@sentry/nextjs";
import type { Session } from "next-auth";

// Headers safe to keep. Everything else (cookie, authorization,
// x-forwarded-for, etc.) is dropped.
const SAFE_HEADER_NAMES = ["content-type", "content-length", "accept", "user-agent"];
const SAFE_HEADERS = new Set(SAFE_HEADER_NAMES);

// `dataCollection` for every runtime's `Sentry.init`. The SDK collects every
// category when this is left unset, so each one is switched off explicitly —
// a hard requirement for a platform that stores minors' data. `scrubEvent`
// still runs after it as the backstop.
export const SENTRY_DATA_COLLECTION: Parameters<typeof Sentry.init>[0]["dataCollection"] = {
  userInfo: false,
  cookies: false,
  httpHeaders: { allow: SAFE_HEADER_NAMES },
  httpBodies: [],
  urlQueryParams: false,
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
};

// Path segments whose FOLLOWING segment is a secret token. e.g. the "abc123" in
// /family/invite/abc123 or /bookings/join/abc123.
const TOKEN_PARENT_SEGMENTS = new Set(["invite", "join"]);

const REDACTED = "[redacted]";

// Sentry event scrubbing — runs as `beforeSend` in every runtime (server,
// edge, client) before any event leaves the process.
//
// Why this exists: Sentry's request integration attaches `request.url`,
// `request.query_string`, and `request.headers` to error events, and its
// built-in filtering does not know which of our URLs carry secrets. Several of our
// routes carry single-use secrets in the URL itself:
//   - reset-password?token=…              (query string)
//   - recap/<noteId>?sig=…                 (query string)
//   - family/invite/<token>                (path segment)
//   - bookings/join/<token>                (path segment)
// An error thrown on any of those would otherwise ship the token to Sentry.
// Because we store minors' data, we strip the query string, redact token-
// bearing path segments, and reduce headers to a safe allowlist.

// Drop the query string and redact any token path segment, without throwing on
// a relative or malformed URL.
function scrubUrl(rawUrl: string): string {
  const queryIndex = rawUrl.indexOf("?");
  const path = queryIndex === -1 ? rawUrl : rawUrl.slice(0, queryIndex);

  const segments = path.split("/");
  for (let i = 1; i < segments.length; i++) {
    if (TOKEN_PARENT_SEGMENTS.has(segments[i - 1]) && segments[i]) {
      segments[i] = REDACTED;
    }
  }

  const scrubbedPath = segments.join("/");
  return queryIndex === -1 ? scrubbedPath : `${scrubbedPath}?${REDACTED}`;
}

function filterHeaders(headers: Record<string, string>): Record<string, string> {
  const safe: Record<string, string> = {};
  for (const key of Object.keys(headers)) {
    if (SAFE_HEADERS.has(key.toLowerCase())) {
      safe[key] = headers[key];
    }
  }
  return safe;
}

// `beforeSend` hook: scrub URL, query string, and headers from the request
// context. Returning the (mutated) event keeps it; the scrub never drops events.
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  const request = event.request;
  if (request) {
    if (typeof request.url === "string") {
      request.url = scrubUrl(request.url);
    }
    // ?token=… / ?sig=… must never be retained.
    delete request.query_string;
    if (request.headers) {
      request.headers = filterHeaders(request.headers);
    }
    // Belt-and-suspenders: SENTRY_DATA_COLLECTION should already keep these
    // out, but never let them through.
    delete request.cookies;
    delete request.data;
  }
  return event;
}

export function setSentryUser(session: Session | null | undefined): void {
  if (!session?.user) {
    // Clear any user the isolation scope may have carried (e.g. public route).
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: session.user.id });

  const organizationId = session.user.organizationId;
  if (organizationId) {
    Sentry.setTag("organizationId", organizationId);
  }
}
